import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail, fileSrc } from "../../lib/apiClient";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Plus, Trash2, ExternalLink, Package, Sparkles, Wand2, X, Search, Clock, BadgeDollarSign } from "lucide-react";
import { toast } from "sonner";

const lbl = "text-xs uppercase tracking-wider text-[#5C5651]";
const fieldCls = "mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]";
const fmt = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const EMPTY = { name: "", vendor: "", vendor_contact: "", price: "", category: "", lead_time: "", link: "", notes: "", image_url: "" };

export default function ProductLibraryTab() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [smartUrl, setSmartUrl] = useState("");
  const [filling, setFilling] = useState(false);
  const [priceReminder, setPriceReminder] = useState(false);
  const [busy, setBusy] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [newCat, setNewCat] = useState("");
  const [detail, setDetail] = useState(null);

  const loadProducts = () => api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  const loadCategories = () => api.get("/product-categories").then((r) => setCategories(r.data)).catch(() => {});
  useEffect(() => { loadProducts(); loadCategories(); }, []);

  const catNames = useMemo(() => categories.map((c) => c.name), [categories]);
  const filtered = useMemo(() => {
    let list = filter === "All" ? products : products.filter((p) => (p.category || "Uncategorized") === filter);
    const t = search.trim().toLowerCase();
    if (t) {
      list = list.filter((p) =>
        [p.name, p.vendor, p.category, p.lead_time, String(p.price || "")]
          .filter(Boolean).some((s) => String(s).toLowerCase().includes(t)));
    }
    return list;
  }, [products, filter, search]);

  const resetForm = () => { setForm(EMPTY); setFile(null); setPreview(null); setSmartUrl(""); setPriceReminder(false); };

  const pickFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f); setPreview(URL.createObjectURL(f)); setForm((s) => ({ ...s, image_url: "" }));
  };

  const smartFill = async () => {
    const url = smartUrl.trim();
    if (!url) { toast.error("Paste a product URL first"); return; }
    setFilling(true);
    try {
      const { data } = await api.post("/products/smart-fill", { url });
      setForm((s) => ({
        ...s,
        name: data.name || s.name,
        vendor: data.vendor || s.vendor,
        price: data.price ? String(data.price) : s.price,
        link: data.link || url,
        image_url: data.image_url || s.image_url,
      }));
      setFile(null);
      setPreview(data.image_url || null);
      setPriceReminder(true);
      const missing = [];
      if (!data.price) missing.push("price");
      if (!data.vendor) missing.push("vendor");
      if (!data.name) missing.push("name");
      if (missing.length) {
        toast.warning(`Imported — couldn't auto-detect ${missing.join(" & ")}. Please add ${missing.length > 1 ? "them" : "it"} manually.`);
      } else {
        toast.success("Details imported — review and save");
      }
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setFilling(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Product name is required"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("vendor", form.vendor || "");
      fd.append("vendor_contact", form.vendor_contact || "");
      fd.append("price", form.price || "0");
      fd.append("category", form.category || "");
      fd.append("lead_time", form.lead_time || "");
      fd.append("link", form.link || "");
      fd.append("notes", form.notes || "");
      fd.append("image_url", file ? "" : (form.image_url || ""));
      if (file) fd.append("file", file);
      await api.post("/products", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Product saved to library");
      resetForm(); setOpen(false); loadProducts();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/products/${id}`); setProducts((p) => p.filter((x) => x.id !== id)); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const addCategory = async (e) => {
    e.preventDefault();
    if (!newCat.trim()) return;
    try { await api.post("/product-categories", { name: newCat.trim() }); setNewCat(""); loadCategories(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const removeCategory = async (id) => {
    try { await api.delete(`/product-categories/${id}`); loadCategories(); loadProducts(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const filterTabs = ["All", ...catNames, ...(products.some((p) => !p.category) ? ["Uncategorized"] : [])];

  return (
    <div className="max-w-6xl space-y-6" data-testid="product-library-tab">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#A68A6B] mb-1">Studio</p>
          <h3 className="font-serif text-3xl text-[#2B2622]">Product Library</h3>
          <p className="text-sm text-[#5C5651] mt-1">Your favorite products, available across every project.</p>
        </div>
        <div className="flex items-center gap-3">
          <Dialog open={catOpen} onOpenChange={setCatOpen}>
            <DialogTrigger asChild>
              <Button data-testid="manage-categories-btn" variant="outline"
                className="rounded-none border-[#A68A6B] text-[#8C7154] hover:bg-[#F2EFEB] uppercase tracking-wider text-xs py-5 px-4">
                Categories
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none border-[#EBE5E0] bg-white">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl text-[#2B2622]">Manage categories</DialogTitle>
                <DialogDescription className="sr-only">Create and delete product categories</DialogDescription>
              </DialogHeader>
              <form onSubmit={addCategory} className="flex gap-2 mt-2">
                <Input data-testid="new-category-input" value={newCat} onChange={(e) => setNewCat(e.target.value)}
                  placeholder="e.g. Lighting" className={`flex-1 ${fieldCls} mt-0`} />
                <Button data-testid="add-category-submit" type="submit"
                  className="rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs px-4">Add</Button>
              </form>
              <div className="mt-4 space-y-2">
                {categories.length === 0 ? (
                  <p className="text-sm text-[#9b938b]">No categories yet.</p>
                ) : categories.map((c) => (
                  <div key={c.id} className="flex items-center justify-between border border-[#EBE5E0] px-3 py-2">
                    <span className="text-sm text-[#2B2622]">{c.name}</span>
                    <button data-testid={`delete-category-${c.id}`} onClick={() => removeCategory(c.id)}
                      className="text-[#5C5651] hover:text-[#9E4747]"><Trash2 size={15} strokeWidth={1.5} /></button>
                  </div>
                ))}
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-product-btn"
                className="rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs py-5 px-5">
                <Plus size={15} className="mr-2" /> Add Product
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none border-[#EBE5E0] bg-white max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl text-[#2B2622]">Add product</DialogTitle>
                <DialogDescription className="sr-only">Add a product to your library, optionally importing from a URL</DialogDescription>
              </DialogHeader>

              <div className="mt-2 border border-[#A68A6B] bg-[#F9F6F1] p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles size={15} className="text-[#A68A6B]" />
                  <span className="text-xs uppercase tracking-wider text-[#8C7154]">Smart import</span>
                </div>
                <p className="text-[12px] text-[#5C5651] mb-2">Paste a vendor product link to auto-fill the photo, name, vendor &amp; price.</p>
                <div className="flex gap-2">
                  <Input data-testid="smart-fill-url-input" type="url" value={smartUrl}
                    onChange={(e) => setSmartUrl(e.target.value)} placeholder="https://www.vendor.com/product…"
                    className={`flex-1 ${fieldCls} mt-0`} />
                  <Button data-testid="smart-fill-btn" type="button" onClick={smartFill} disabled={filling}
                    className="rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs px-4">
                    <Wand2 size={14} className="mr-1.5" /> {filling ? "Fetching…" : "Fetch"}
                  </Button>
                </div>
              </div>

              {priceReminder && (
                <div data-testid="confirm-details-reminder"
                  className="mt-3 flex items-start gap-2 border border-[#C49A4A] bg-[#FBF6EA] px-4 py-3 animate-in fade-in slide-in-from-top-1">
                  <BadgeDollarSign size={18} className="text-[#B8862F] shrink-0 mt-0.5" />
                  <p className="text-[13px] text-[#7A5E2E] leading-snug flex-1">
                    Details imported — <strong>please confirm they're correct</strong> (especially price &amp; vendor) before saving to your library.
                  </p>
                  <button type="button" data-testid="dismiss-details-reminder" onClick={() => setPriceReminder(false)}
                    className="text-[#B8862F] hover:text-[#7A5E2E] shrink-0"><X size={15} /></button>
                </div>
              )}

              <form onSubmit={save} className="space-y-4 mt-4">
                <div>
                  <Label className={lbl}>Photo</Label>
                  {(preview || form.image_url) && (
                    <div className="relative mt-2 w-full">
                      <img src={preview || fileSrc(form.image_url)} alt="preview" data-testid="product-image-preview"
                        className="w-full max-h-52 object-cover border border-[#EBE5E0]" />
                      <button type="button" onClick={() => { setFile(null); setPreview(null); setForm((s) => ({ ...s, image_url: "" })); }}
                        className="absolute top-2 right-2 bg-white/90 p-1 text-[#9E4747]"><X size={14} /></button>
                    </div>
                  )}
                  <input data-testid="product-file-input" type="file" accept="image/*" onChange={pickFile}
                    className="mt-2 block w-full text-sm text-[#5C5651] file:mr-3 file:border file:border-[#A68A6B] file:bg-transparent file:text-[#A68A6B] file:px-4 file:py-2 file:uppercase file:text-xs file:tracking-wider" />
                </div>
                <div>
                  <Label className={lbl}>Product name</Label>
                  <Input data-testid="product-name-input" required value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })} className={fieldCls} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className={lbl}>Vendor</Label>
                    <Input data-testid="product-vendor-input" value={form.vendor}
                      onChange={(e) => setForm({ ...form, vendor: e.target.value })} className={fieldCls} />
                  </div>
                  <div>
                    <Label className={lbl}>Price</Label>
                    <Input data-testid="product-price-input" type="number" step="0.01" value={form.price}
                      onChange={(e) => setForm({ ...form, price: e.target.value })} className={fieldCls} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className={lbl}>Vendor contact</Label>
                    <Input data-testid="product-vendor-contact-input" placeholder="rep name / email / phone" value={form.vendor_contact}
                      onChange={(e) => setForm({ ...form, vendor_contact: e.target.value })} className={fieldCls} />
                  </div>
                  <div>
                    <Label className={lbl}>Lead time</Label>
                    <Input data-testid="product-lead-time-input" placeholder="e.g. 6–8 weeks" value={form.lead_time}
                      onChange={(e) => setForm({ ...form, lead_time: e.target.value })} className={fieldCls} />
                  </div>
                </div>
                <div>
                  <Label className={lbl}>Category</Label>
                  <Select value={form.category || "none"} onValueChange={(v) => setForm({ ...form, category: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="product-category-input" className="mt-1 rounded-none border-[#EBE5E0] focus:ring-0"><SelectValue placeholder="Uncategorized" /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      <SelectItem value="none">Uncategorized</SelectItem>
                      {catNames.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className={lbl}>Product link</Label>
                  <Input data-testid="product-link-input" type="url" value={form.link}
                    onChange={(e) => setForm({ ...form, link: e.target.value })} className={fieldCls} />
                </div>
                <div>
                  <Label className={lbl}>Notes</Label>
                  <Textarea data-testid="product-notes-input" value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })} className={fieldCls} />
                </div>
                <Button data-testid="save-product-submit" type="submit" disabled={busy}
                  className="w-full rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs py-5">
                  {busy ? "Saving…" : "Save to Library"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {filterTabs.length > 1 && (
        <div className="flex flex-wrap gap-2 border-b border-[#EBE5E0] pb-3">
          {filterTabs.map((c) => (
            <button key={c} data-testid={`product-filter-${c}`} onClick={() => setFilter(c)}
              className={`text-xs uppercase tracking-wider px-3 py-1.5 border ${filter === c ? "border-[#A68A6B] bg-[#A68A6B] text-white" : "border-[#EBE5E0] text-[#5C5651] hover:border-[#A68A6B]"}`}>
              {c}
            </button>
          ))}
        </div>
      )}

      <div className="relative max-w-md">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A68A6B]" />
        <Input data-testid="product-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by brand, category, price or lead time…"
          className="pl-9 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
      </div>

      {filtered.length === 0 ? (
        <div className="border border-dashed border-[#D9CBBF] bg-white p-12 text-center text-[#5C5651] flex flex-col items-center gap-3">
          <Package size={28} strokeWidth={1.25} className="text-[#C9BBAE]" />
          No products yet. Add a product or use Smart import to build your library.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((p) => (
            <div key={p.id} className="border border-[#EBE5E0] bg-white flex flex-col group" data-testid={`product-card-${p.id}`}>
              <div className="relative bg-[#F2EFEB] h-48 overflow-hidden cursor-pointer" data-testid={`product-open-${p.id}`} onClick={() => setDetail(p)}>
                {p.image_url
                  ? <img src={fileSrc(p.image_url)} alt={p.name} className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center text-[#C9BBAE]"><Package size={32} strokeWidth={1} /></div>}
                <button data-testid={`delete-product-${p.id}`} onClick={(e) => { e.stopPropagation(); remove(p.id); }}
                  className="absolute top-2 right-2 bg-white/90 hover:bg-white text-[#5C5651] hover:text-[#9E4747] p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Trash2 size={15} strokeWidth={1.5} />
                </button>
              </div>
              <div className="p-4 flex-1 cursor-pointer" onClick={() => setDetail(p)}>
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm text-[#2B2622] font-medium leading-snug">{p.name}</p>
                  {p.link && (
                    <a href={p.link} target="_blank" rel="noopener noreferrer" data-testid={`product-link-${p.id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="text-[#A68A6B] hover:text-[#8C7154] shrink-0"><ExternalLink size={14} /></a>
                  )}
                </div>
                {p.vendor && <p className="text-[11px] text-[#9b938b] mt-0.5">{p.vendor}</p>}
                {p.vendor_contact && <p className="text-[11px] text-[#9b938b]">{p.vendor_contact}</p>}
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {p.price > 0 && <span className="text-sm text-[#8C7154]">{fmt(p.price)}</span>}
                  {p.category && <span className="text-[10px] uppercase tracking-wider text-[#A68A6B] border border-[#EBE5E0] px-1.5 py-0.5">{p.category}</span>}
                  {p.lead_time && <span className="text-[11px] text-[#9b938b] inline-flex items-center gap-1"><Clock size={11} />{p.lead_time}</span>}
                </div>
                {p.notes && <p className="text-[12px] text-[#5C5651] mt-2 leading-relaxed whitespace-pre-line">{p.notes}</p>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="rounded-none border-[#EBE5E0] bg-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="product-detail-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#2B2622]">{detail?.name}</DialogTitle>
            <DialogDescription className="sr-only">Expanded product details</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 mt-1">
              {detail.image_url
                ? <img src={fileSrc(detail.image_url)} alt={detail.name} data-testid="product-detail-image"
                    className="w-full max-h-72 object-cover border border-[#EBE5E0]" />
                : <div className="w-full h-40 bg-[#F2EFEB] flex items-center justify-center text-[#C9BBAE]"><Package size={36} strokeWidth={1} /></div>}
              <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                <div>
                  <p className={lbl}>Price</p>
                  <p className="text-[#2B2622] mt-0.5">{detail.price > 0 ? fmt(detail.price) : "—"}</p>
                </div>
                <div>
                  <p className={lbl}>Category</p>
                  <p className="text-[#2B2622] mt-0.5">{detail.category || "Uncategorized"}</p>
                </div>
                <div>
                  <p className={lbl}>Vendor</p>
                  <p className="text-[#2B2622] mt-0.5">{detail.vendor || "—"}</p>
                </div>
                <div>
                  <p className={lbl}>Lead time</p>
                  <p className="text-[#2B2622] mt-0.5">{detail.lead_time || "—"}</p>
                </div>
                <div className="col-span-2">
                  <p className={lbl}>Vendor contact</p>
                  <p className="text-[#2B2622] mt-0.5">{detail.vendor_contact || "—"}</p>
                </div>
              </div>
              {detail.notes && (
                <div>
                  <p className={lbl}>Notes</p>
                  <p className="text-sm text-[#2B2622] mt-0.5 leading-relaxed whitespace-pre-line">{detail.notes}</p>
                </div>
              )}
              {detail.link && (
                <a href={detail.link} target="_blank" rel="noopener noreferrer" data-testid="product-detail-open-link"
                  className="inline-flex items-center gap-2 border border-[#A68A6B] text-[#8C7154] hover:bg-[#F2EFEB] px-4 py-2.5 text-xs uppercase tracking-wider">
                  <ExternalLink size={14} /> Open product page
                </a>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
